import React from 'react';
import { maxReportNumberDecimalPlaces, pageSize } from './ReportsConstants';
import { FmlxRadioButton } from 'fmlx-common-ui';
import { LayerDTO, ScreenLayerDTO } from 'business/models/Models';

export function createPdfWithPopupBlockerHandler(
  pdfMakeInstance,
  pdfDefinition,
  fileName,
  saveReport,
  onPopupBlocked,
) {
  if (saveReport) {
    pdfMakeInstance.createPdf(pdfDefinition).download(`${fileName}.pdf`);
  } else {
    const pdfWindow = window.open('', '_blank');
    if (!pdfWindow) {
      if (onPopupBlocked) {
        onPopupBlocked();
      }
      return;
    }
    pdfMakeInstance.createPdf(pdfDefinition).open({}, pdfWindow);
  }
}

export function openOrSavePdfBlob(
  blob: Blob,
  fileName: string,
  saveReport: boolean,
  onPopupBlocked?: () => void,
) {
  const url = URL.createObjectURL(blob);
  const downloadName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;

  if (saveReport) {
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return;
  }

  const pdfWindow = window.open('', '_blank');
  if (!pdfWindow) {
    URL.revokeObjectURL(url);
    onPopupBlocked?.();
    return;
  }

  pdfWindow.location.href = url;
}

export function showPopupBlockedModal(
  showModalSimple,
  setPopupBlockedModal,
  hideModal,
  message = 'Unable to preview report. Please make sure pop-ups and redirects are allowed then try again.',
  title = 'Report Preview Failed',
) {
  showModalSimple({
    body: message,
    customHeader: (
      <div className="px-6 py-4 flex items-center font-roboto text-xl leading-[120%] font-normal text-[#f7a246]">
        {title}
      </div>
    ),
    okButtonText: 'OK',
    isOkOnly: true,
    hideCloseButton: true,
    onOk: () => {
      if (setPopupBlockedModal) {
        setPopupBlockedModal(false);
      }
      hideModal();
    },
  });
}

export function roundToMaxDecimals(value: number): number {
  const factor = Math.pow(10, maxReportNumberDecimalPlaces);
  return Math.round(value * factor) / factor;
}

export const IMAGER_TEMP_PROP_NAME = 'ImagerTemperature';
export const IMAGER_TEMP_NAME = 'Imager Temperature';

export const WELL_SOLUTION_LABEL = 'Well Solution';

export const INGREDIENT_NAME_SUFFIX: Record<string, string> = {
  Protein: ' p',
  [WELL_SOLUTION_LABEL]: ' w',
  Fragment: ' f',
  Seed: ' s',
  Additive: ' a',
};

export const ingredientNamePreferenceOptions: {
  content: string;
  hint: string;
  type: 'information' | 'error' | 'warning' | FmlxRadioButton.HintType;
  disabled: boolean;
}[] = [
  { content: 'Long Ingredient Name', disabled: false, hint: '', type: 'information' },
  { content: 'Short Ingredient Name', disabled: false, hint: '', type: 'information' },
];

export type IngReportTableCell = {
  text?: string;
  bold?: boolean;
  fillColor?: string;
  color?: string;
  alignment?: string;
  colSpan?: number;
  border?: boolean[];
  columns?: any[];
  table?: any;
  margin?: number[];
  width?: string | number;
  stack?: any[];
  style?: string;
  fontSize?: number;
  lineHeight?: number;
  [key: string]: any;
};

export type IngReportTableRow = IngReportTableCell[];

export type IngReportTableRows = IngReportTableRow[];

export const getScreenNamesFromLayers = (layers?: LayerDTO[]): string[] => {
  return [
    ...new Set(
      (layers ?? []).flatMap((lyr) =>
        lyr.layerDetail instanceof ScreenLayerDTO &&
        lyr.layerDetail?.screenLot?.experiment?.expName != null
          ? [lyr.layerDetail?.screenLot?.experiment?.expName ?? '']
          : [],
      ),
    ),
  ];
};

export type ScreenNameEntry = { label: string; value: string };

export const getScreenNameEntries = (screenNames: string[]): ScreenNameEntry[] =>
  screenNames?.length > 1
    ? screenNames.map((name, idx) => ({ label: `Screen ${idx + 1}: `, value: name }))
    : [{ label: 'Screen: ', value: screenNames?.at(0) ?? '-' }];

export const AVG_CHAR_WIDTH_FACTOR = 0.46;

export const estimateCharsPerLine = (availableWidth: number, fontSize: number): number =>
  Math.max(1, Math.floor(availableWidth / (fontSize * AVG_CHAR_WIDTH_FACTOR)));

const TAB_VISUAL_WIDTH_CHARS = 4;

export function getScreenNameLineRuns(screenNames: string[], maxLineChars: number) {
  const entries = getScreenNameEntries(screenNames);
  const runs: PDFTextProps[] = [];
  let lineLength = 0;

  entries.forEach(({ label, value }, idx) => {
    const entryLength = label.length + value.length;
    if (idx === 0) {
      lineLength = entryLength;
    } else {
      const withSeparator = lineLength + TAB_VISUAL_WIDTH_CHARS + entryLength;
      if (withSeparator > maxLineChars) {
        runs.push({ text: '\n' });
        lineLength = entryLength;
      } else {
        runs.push({ text: '\t' });
        lineLength = withSeparator;
      }
    }
    runs.push({ text: label, bold: true }, { text: value });
  });

  return runs;
}

export function getFieldRuns(fields: PDFLabelledTextProps[], separator: string) {
  const runs: PDFTextProps[] = [];
  fields.forEach(({ label, value }, idx) => {
    if (idx > 0) {
      runs.push({ text: separator });
    }
    runs.push({ text: label, bold: true }, { text: String(value) });
  });
  return runs;
}

export function estimateRunsLineCount(runs: PDFTextProps[], maxLineChars: number) {
  const logicalLines = runs
    .map((run) => run.text)
    .join('')
    .split('\n');
  return logicalLines.reduce((total, line) => {
    const visualLength = line.replace(/\t/g, ' '.repeat(TAB_VISUAL_WIDTH_CHARS)).length;
    return total + Math.max(1, Math.ceil(visualLength / maxLineChars));
  }, 0);
}

export const ROBOTO_LINE_HEIGHT_RATIO = 1.171875;

export const DESIGN_HEADER_FIELD_SEPARATOR = '   |   ';

export function getDesignHeaderRuns(
  expPath: string,
  plateId: number | string,
  barcode: string,
  screenNameRuns: PDFTextProps[],
) {
  const infoRuns = getFieldRuns(
    [
      { label: 'Experiment: ', value: expPath },
      { label: 'PlateID: ', value: plateId },
      { label: 'Plate Barcode: ', value: barcode },
    ],
    DESIGN_HEADER_FIELD_SEPARATOR,
  );
  return [...infoRuns, { text: '\n' }, ...screenNameRuns];
}

export function getScreenNameColumnCharLimits(
  pageMargin: number,
  fontSize: number,
  columns = 3,
) {
  const contentWidth = pageSize.a4.width - pageMargin * 2;
  const columnWidth = contentWidth / columns;
  return {
    short: estimateCharsPerLine(columnWidth, fontSize),
    medium: estimateCharsPerLine(columnWidth * 2, fontSize),
  };
}

export function getScreenNameColumnsTable(
  screenNames: string[],
  style: string,
  shortMaxLength: number,
  mediumMaxLength: number,
  columns = 3,
) {
  const entries = getScreenNameEntries(screenNames);

  const getSpan = (label: string, value: string) => {
    const length = label.length + value.length;
    if (length <= shortMaxLength) {
      return 1;
    }
    if (length <= mediumMaxLength) {
      return 2;
    }
    return columns;
  };

  const rows = [];
  let currentRow = [];
  let currentSpan = 0;

  const padRowToFullWidth = () => {
    while (currentSpan < columns) {
      currentRow.push({});
      currentSpan++;
    }
  };

  entries.forEach(({ label, value }) => {
    const span = getSpan(label, value);
    if (currentSpan + span > columns) {
      padRowToFullWidth();
      rows.push(currentRow);
      currentRow = [];
      currentSpan = 0;
    }
    currentRow.push({
      text: [{ text: label, bold: true }, { text: value }],
      colSpan: span,
      style,
    });
    for (let i = 1; i < span; i++) {
      currentRow.push({});
    }
    currentSpan += span;
  });

  if (currentRow.length > 0) {
    padRowToFullWidth();
    rows.push(currentRow);
  }

  return {
    table: {
      widths: Array(columns).fill('*'),
      body: rows,
    },
    layout: 'noBorders',
  };
}

export const BASE_PAGE_MARGINS = [30, 50, 30, 30];
export const HEADER_ROW_MARGIN = [20, 20, 20, 10]; // left, top, right, bottom
export const HEADER_LEADING_COLUMNS_WIDTH = 120;
export const HEADER_FONT_SIZE = 8;
export const HEADER_LINE_HEIGHT = 1.5;
export const MAX_TOP_MARGIN = pageSize.a4.width * 0.5;
export const AVAILABLE_HEADER_WIDTH =
  pageSize.a4.height -
  HEADER_ROW_MARGIN[0] -
  HEADER_ROW_MARGIN[2] -
  HEADER_LEADING_COLUMNS_WIDTH;
export const HEADER_MAX_LINE_CHARS = estimateCharsPerLine(
  AVAILABLE_HEADER_WIDTH,
  HEADER_FONT_SIZE,
);

export const DESIGN_LABEL_FONT_SIZE = 7;
export const DESIGN_LABEL_LINE_HEIGHT = 1.2;
export const IMAGE_INFO_FONT_SIZE = 7;
export const IMAGE_INFO_FIELD_SEPARATOR = '\t';
export const INGREDIENT_INFO_FONT_SIZE = 7;
export const INGREDIENT_INFO_FIELD_SEPARATOR = '   ';

export const HIT_REPORT_EXPERIMENT_LABEL_FONT_SIZE = 12;
export const HIT_REPORT_PAGE_MARGIN = 50; // left + right pageMargins
export const {
  short: HIT_REPORT_SCREEN_NAME_SHORT_MAX_LENGTH,
  medium: HIT_REPORT_SCREEN_NAME_MEDIUM_MAX_LENGTH,
} = getScreenNameColumnCharLimits(
  HIT_REPORT_PAGE_MARGIN,
  HIT_REPORT_EXPERIMENT_LABEL_FONT_SIZE,
);

export const IMAGE_REPORT_INFO_FONT_SIZE = 7;
export const IMAGE_REPORT_INFO_FIELD_SEPARATOR = '\t';
export const IMAGE_REPORT_INFO_MARGIN = 20; // left + right pageMargins

export const INGREDIENT_REPORT_INFO_FONT_SIZE = 7;
export const INGREDIENT_REPORT_INFO_MARGIN = 10; // left + right pageMargins

export const INSTANT_DROP_EXPERIMENT_LABEL_FONT_SIZE = 12;
export const INSTANT_DROP_PAGE_MARGIN = 50; // left + right pageMargins
export const {
  short: INSTANT_DROP_SCREEN_NAME_SHORT_MAX_LENGTH,
  medium: INSTANT_DROP_SCREEN_NAME_MEDIUM_MAX_LENGTH,
} = getScreenNameColumnCharLimits(
  INSTANT_DROP_PAGE_MARGIN,
  INSTANT_DROP_EXPERIMENT_LABEL_FONT_SIZE,
);

export const SINGLE_DROP_INFO_FONT_SIZE = 8;
export const SINGLE_DROP_INFO_FIELD_SEPARATOR = ' - ';
export const SINGLE_DROP_PAGE_MARGIN = 25; // left + right pageMargins
export const {
  short: SINGLE_DROP_SCREEN_SHORT_MAX_LENGTH,
  medium: SINGLE_DROP_SCREEN_MEDIUM_MAX_LENGTH,
} = getScreenNameColumnCharLimits(SINGLE_DROP_PAGE_MARGIN, SINGLE_DROP_INFO_FONT_SIZE);

export const WELL_REPORT_INFO_FONT_SIZE = 8;
export const WELL_REPORT_INFO_FIELD_SEPARATOR = ' - ';
export const WELL_REPORT_PAGE_MARGIN = 50; // left + right pageMargins
export const {
  short: WELL_REPORT_SCREEN_SHORT_MAX_LENGTH,
  medium: WELL_REPORT_SCREEN_MEDIUM_MAX_LENGTH,
} = getScreenNameColumnCharLimits(WELL_REPORT_PAGE_MARGIN, WELL_REPORT_INFO_FONT_SIZE);

export type PDFTextProps = {
  text: string;
  bold?: boolean;
};

export type PDFLabelledTextProps = {
  label: string;
  value: unknown;
};
